<script lang="ts">
import type { SeminarCartModule } from '~/store/seminarCart'

interface ToccoDataPaths {
  course_price?: { value: number }
  registration?: { value: string }
  participation_max?: { value: string }
}

interface ParsedToccoData {
  paths: ToccoDataPaths
}

const parseToccoData = (data: string | object | null | undefined): ParsedToccoData => {
  if (!data) return { paths: {} }
  if (typeof data === 'object') return data as ParsedToccoData
  try {
    return JSON.parse(data) as ParsedToccoData
  } catch {
    return { paths: {} }
  }
}

export const buildSeminarModulesPayload = (modules: readonly SeminarCartModule[]) =>
  modules.map((module) => {
    const toccoData = parseToccoData(module.fieldToccoData)
    return {
      id: module.fieldExternalId,
      waitingList:
        parseInt(toccoData.paths?.registration?.value ?? '0') >=
        parseInt(toccoData.paths?.participation_max?.value ?? '0'),
    }
  })
</script>

<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { useGtm } from '@gtm-support/vue-gtm'
import { format, parse } from 'date-fns'
import {
  TaxonomyTermToccoCountryFieldsFragment,
  TermsByNameDocument,
  TermsDocument,
  ConfigSettingsDocument,
  TaxonomyTermToccoCooperationPartner,
} from '~/api/graphql/generated'
import type { TermsByNameQueryVariables, TermsQueryVariables } from '~/api/graphql/generated'
import { Option } from '~/components/atoms/forms/select/Select.vue'
import { FormKitMessages } from '@formkit/vue'
import { useRegistrationFormSubmit } from '~/api/drupal'
import type { FormKitFile } from '@formkit/inputs'
import { useTypedAsyncQuery } from '~/lib/utils/graphqlDocument'
import { formatToccoPrice } from '~/lib/utils/toccoUtils'
import { useI18n } from 'vue-i18n'

export interface AddressProps {
  street_address: string
  po_box: string
  postal_code: string
  city: string
  country: number
}

export interface AddressExtendedProps extends AddressProps {
  name: string
  hideCompany: boolean
}

export interface InvoiceAddressProps extends AddressProps {
  invoiceAddressActive: string
  department: string
  billing_information: string
}

export interface RegistrationSeminarProps {
  personalInfo: {
    salutation: string
    firstname: string
    lastname: string
    email: string
    birthdate: string
    nationality: string
    language: string
    place_of_origin: string
    mobile: string
    phone: string
    position: string
    profession: string
  }
  privateAddress: AddressProps
  company: AddressExtendedProps
  invoiceAddress: InvoiceAddressProps
  message: string
  upload: FormKitFile[]
  uploadIds: {
    'upload-0': string
    'upload-1': string
    'upload-2': string
  }
}

const props = defineProps<{
  formData: RegistrationSeminarProps
}>()

const emit = defineEmits(['cancel'])
const { languageCODE } = useLocale()
const { t } = useI18n()
const store = useSeminarCartStore()
const gtm = useGtm()
const { subscribe: subscribeNewsletter } = useCwbNewsletterSubscribe()

const reviewFormData = ref()

const seminar = computed(() => store.seminar)
const modules = computed(() => store.modules)
const hasCooperationPartner = ref(false)

const payload = computed(() => ({
  personalInfo: props.formData.personalInfo,
  company: props.formData.company,
  invoiceAddress: props.formData.invoiceAddress,
  privateAddress: props.formData.privateAddress,
  seminar: seminar.value?.fieldExternalId,
  modules: buildSeminarModulesPayload(modules.value),
  uploadOne: props.formData.uploadIds['upload-0'] ? props.formData.uploadIds['upload-0'] : '',
  uploadTwo: props.formData.uploadIds['upload-1'] ? props.formData.uploadIds['upload-1'] : '',
  uploadThree: props.formData.uploadIds['upload-2'] ? props.formData.uploadIds['upload-2'] : '',
  cooperationPartnerId: hasCooperationPartner.value ? (reviewFormData.value?.cooperationPartner ?? null) : null,
  message: props.formData.message,
}))

defineExpose({ payload })

const termsVariables: TermsQueryVariables = {
  limit: 250,
  vocabulary: 'tocco_country',
  language: languageCODE.value,
}
const { data: termsData } = await useTypedAsyncQuery(TermsDocument, termsVariables)

const getCountryName = (externalId: number) => {
  const entities = termsData.value?.taxonomyTermQuery?.entities as TaxonomyTermToccoCountryFieldsFragment[]
  const foundEntity = entities.filter((entity: TaxonomyTermToccoCountryFieldsFragment) => {
    const fieldExternalId = entity?.fieldExternalId ?? 0
    return fieldExternalId === externalId
  })

  return foundEntity[0]?.name
}

const { data: configToccoSettings } = await useTypedAsyncQuery(ConfigSettingsDocument)

const pages = computed(() => {
  return {
    termsConditions: configToccoSettings.value?.configCareumToccoSettings?.termsAndConditionsPage ?? '/',
    privacyPolicy: configToccoSettings.value?.configCareumToccoSettings?.privacyPolicyPage ?? '/',
    thankYou: configToccoSettings.value?.configCareumToccoSettings?.seminarThankYouPage ?? '/',
  }
})

const legal = computed(() => {
  return {
    termsConditions: {
      label: t('tocco.registration.seminar.review.termsConditions.text', {
        hyperlink: `<a href="${pages.value.termsConditions}" target="_blank">
        ${t('tocco.registration.seminar.review.termsConditions.hyperlink')}
      </a>`,
      }),
      error: t('tocco.registration.seminar.review.termsConditions.error'),
    },

    privacyPolicy: {
      label: t('tocco.registration.seminar.review.privacyPolicy.text', {
        hyperlink: `<a href="${pages.value.privacyPolicy}" target="_blank">
        ${t('tocco.registration.seminar.review.privacyPolicy.hyperlink')}
      </a>`,
      }),
      error: t('tocco.registration.seminar.review.privacyPolicy.error'),
    },
  }
})

const totalPrice = computed(() =>
  modules.value.reduce((sum, module) => {
    const toccoData = parseToccoData(module.fieldToccoData)
    const price = parseFloat(String(toccoData.paths?.course_price?.value ?? ''))
    return sum + (Number.isNaN(price) ? 0 : price)
  }, 0),
)

const totalFormattedPrice = computed(() => formatToccoPrice(totalPrice.value))

const formattedBirthdate = computed(() =>
  props.formData.personalInfo.birthdate
    ? format(parse(props.formData.personalInfo.birthdate, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')
    : '',
)

const actualAddress: ComputedRef<AddressProps | InvoiceAddressProps> = computed(() =>
  props.formData.invoiceAddress.invoiceAddressActive === 'privateAddress'
    ? props.formData.privateAddress
    : props.formData.invoiceAddress.invoiceAddressActive === 'company'
      ? props.formData.company
      : props.formData.invoiceAddress,
)

const coopPartnersVariables: TermsByNameQueryVariables = {
  vocabulary: 'tocco_cooperation_partner',
  language: languageCODE.value,
  limit: 250,
}
const { data: coopPartnersData } = await useTypedAsyncQuery(TermsByNameDocument, coopPartnersVariables)

const cooperationPartnerOptions = reactive({
  entities: (coopPartnersData.value?.taxonomyTermQuery?.entities as TaxonomyTermToccoCooperationPartner[])?.map(
    (entity): Option => ({
      label: entity?.name ?? '',
      value: entity?.fieldExternalId ?? '',
    }),
  ),
})

const error = ref(false)
const isSubmitting = ref(false)

const cancel = () => {
  emit('cancel')
}

const handleSubmit = async () => {
  if (!modules.value.length || isSubmitting.value) {
    return
  }
  isSubmitting.value = true

  const clonedPayload = JSON.parse(JSON.stringify(payload.value))
  clonedPayload.personalInfo.phone = clonedPayload.personalInfo.phone.replace(/\s+/g, '')
  clonedPayload.personalInfo.mobile = clonedPayload.personalInfo.mobile.replace(/\s+/g, '')

  gtm?.trackEvent({
    event: 'Registration_CWB',
    cwb_type: 'seminar',
    cwb_title: seminar.value?.title,
  })

  try {
    await useRegistrationFormSubmit('/tocco/register/seminar', JSON.stringify(clonedPayload))

    if (reviewFormData.value?.newsletter) {
      try {
        await subscribeNewsletter(clonedPayload.personalInfo)
      } catch (e) {
        console.log(e)
      }
    }

    navigateTo({ path: pages.value.thankYou })
  } catch {
    error.value = true
    isSubmitting.value = false

    if (import.meta.client) {
      setTimeout(() => window.scrollTo(0, 0), 40)
    }
  }
}
</script>

<template>
<FormKitLazyProvider config-file="true">

  <FormKit v-model="reviewFormData" type="form" :actions="false" @submit="handleSubmit">
    <div v-animate="{ animation: 'vertical-reveal' }" class="form-title">
      <h6>
        {{ t('tocco.registration.seminar.headings.subtitle') }}
      </h6>
      <h1>
        {{ t('tocco.registration.seminar.review.headings.title') }}
      </h1>
      <div v-if="error" class="form-error">
        <div class="icon">
          <Icon name="alert" />
        </div>
        <p>
          {{ t('tocco.registration.conference.review.error') }}
        </p>
      </div>
    </div>

    <div class="grid-columns-2">
      <div v-animate="{ animation: 'vertical-reveal' }" class="left">
        <h4>
          {{ t('tocco.registration.seminar.review.personalInfo.title') }}
        </h4>
        <div class="details-wrapper first">
          <p class="label">
            {{ t('tocco.registration.seminar.review.personalInfo.name') }}
          </p>
          <p class="label-information">
            <template v-if="String(formData.personalInfo.salutation) === '66'">
              {{ formData.personalInfo.firstname }} {{ formData.personalInfo.lastname }}
            </template>
            <template v-else>
              {{ t(`tocco.registration.personalInfo.salutationOptions.${formData.personalInfo.salutation}`) }}
              {{ formData.personalInfo.firstname }} {{ formData.personalInfo.lastname }}
            </template>
          </p>
        </div>

        <div class="details-wrapper">
          <p class="label">
            <span v-show="formData.personalInfo.mobile">
              {{ t('tocco.registration.seminar.review.personalInfo.mobile') }}
            </span>
            <span v-show="formData.personalInfo.phone">
              {{ t('tocco.registration.seminar.review.personalInfo.phone') }}
            </span>
          </p>
          <p class="label-information">
            <span v-show="formData.personalInfo.mobile">{{ formData.personalInfo.mobile }}</span>
            <span v-show="formData.personalInfo.phone">{{ formData.personalInfo.phone }}</span>
          </p>
        </div>
        <div class="details-wrapper">
          <p class="label">
            {{ t('tocco.registration.seminar.review.personalInfo.email') }}
          </p>
          <p class="label-information">
            {{ formData.personalInfo.email }}
          </p>
        </div>
        <div class="details-wrapper">
          <p class="label">
            {{ t('tocco.registration.seminar.review.personalInfo.address') }}
          </p>
          <p class="label-information">
            <span>{{ formData.privateAddress.street_address }}</span>
            <span>
              {{ formData.privateAddress.postal_code }}
              {{ formData.privateAddress.city }}
            </span>
            <span>
              {{ getCountryName(formData.privateAddress.country) }}
            </span>
          </p>
        </div>
        <div v-show="formData.personalInfo.position" class="details-wrapper">
          <p class="label">
            {{ t('tocco.registration.seminar.review.personalInfo.position') }}
          </p>
          <p class="label-information">
            {{ formData.personalInfo.position }}
          </p>
          <p v-show="formData.personalInfo.profession" class="label">
            {{ t('tocco.registration.seminar.review.personalInfo.profession') }}
          </p>
          <p v-if="formData.personalInfo.profession" class="label-information">
            {{ formData.personalInfo.profession }}
          </p>
        </div>
        <div v-show="formData.personalInfo.birthdate" class="details-wrapper">
          <p class="label">
            {{ t('tocco.registration.seminar.review.personalInfo.birthdate') }}
          </p>
          <p class="label-information">
            {{ formattedBirthdate }}
          </p>
        </div>
        <div v-show="formData.message" class="details-wrapper">
          <p class="label">
            {{ t('tocco.registration.seminar.review.personalInfo.message') }}
          </p>
          <p class="label-information">
            {{ formData.message }}
          </p>
        </div>
      </div>

      <div v-animate="{ animation: 'vertical-reveal' }" class="right">
        <div v-show="!formData.company.hideCompany" class="top-right">
          <h4>
            {{ t('tocco.registration.seminar.review.company.title') }}
          </h4>
          <div class="details-wrapper first">
            <p class="label">
              {{ t('tocco.registration.seminar.review.company.name') }}
            </p>
            <p class="label-information">
              {{ formData.company.name }}
            </p>
          </div>
          <div class="details-wrapper">
            <p class="label">
              {{ t('tocco.registration.seminar.review.company.address') }}
            </p>
            <p class="label-information">
              <span>{{ formData.company.street_address }}</span>

              <span>{{ formData.company.postal_code }} {{ formData.company.city }}</span>

              <span> {{ getCountryName(formData.company.country) }}</span>
            </p>
          </div>
        </div>
        <div class="bottom-right">
          <h4>
            {{ t('tocco.registration.seminar.review.invoiceAddress.title') }}
          </h4>
          <div class="details-wrapper first">
            <p class="label">
              {{ t('tocco.registration.seminar.review.invoiceAddress.address') }}
            </p>
            <p class="label-information">
              <span>{{ actualAddress.street_address }}</span>
              <span>
                {{ actualAddress.postal_code }}
                {{ actualAddress.city }}
              </span>
              <span> {{ getCountryName(actualAddress.country) }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-animate="{ animation: 'vertical-reveal' }" class="summary-producs-wrapper">
      <h4>
        {{ t('tocco.registration.seminar.review.products.heading') }}
      </h4>
      <div class="product-header">
        <h5 class="product-label">
          {{ t('tocco.registration.seminar.review.products.title') }}
        </h5>
        <h5 class="product-price-label">
          {{ t('tocco.registration.seminar.review.products.price') }}
        </h5>
      </div>

      <ProductReview v-for="(module, index) in modules" :key="module.id ?? index" :entity="module" />

      <div class="total-price">
        <p>
          {{ t('tocco.registration.seminar.review.products.totalLabel') }}
        </p>
        <div class="right-item">
          <span class="price-label">{{ totalFormattedPrice }}</span>
        </div>
      </div>
    </div>

    <div v-animate="{ animation: 'vertical-reveal' }" class="select-partner-wrapper">
      <div class="form-section">
        <Checkbox
          id="seminar-review-has-cooperation-partner"
          v-model="hasCooperationPartner"
          name="hasCooperationPartner"
          :label="t('tocco.registration.seminar.review.cooperationPartner')"
          :required="false"
        />
        <Select
          v-show="hasCooperationPartner"
          id="seminar-review-cooperation-partner"
          name="cooperationPartner"
          :options="cooperationPartnerOptions.entities"
          :label="t('tocco.registration.seminar.review.cooperationPartnerLabel')"
          validation="optional|min:1"
          :required="false"
        />
        <Checkbox
          id="seminar-review-terms-conditions"
          name="termsConditions"
          :label="legal.termsConditions.label"
          :required="true"
          :html-label="true"
          :validation-messages="{ accepted: legal.termsConditions.error }"
        />
        <Checkbox
          id="seminar-review-privacy-policy"
          name="privacyPolicy"
          :label="legal.privacyPolicy.label"
          :required="true"
          :html-label="true"
          :validation-messages="{ accepted: legal.privacyPolicy.error }"
        />
        <Checkbox
          id="seminar-review-newsletter"
          name="newsletter"
          :label="t('tocco.registration.seminar.review.newsletter_consent')"
          :required="false"
        />
      </div>
    </div>

    <div v-animate="{ animation: 'vertical-reveal' }" class="buttons-wrapper">
      <FormKitMessages />
      <Button secondary slide class="cancel-button" @click="cancel()">
        {{ t('tocco.registration.seminar.review.cancel') }}
      </Button>
      <div>
        <p v-if="modules.length === 0" class="selection-hint">
          {{ t('tocco.runSelection.selectAtLeastOne') }}
        </p>
        <Button primary slide class="submit-button" :disabled="!modules.length">
          {{ t('tocco.registration.seminar.review.submit') }}
        </Button>
      </div>
    </div>
  </FormKit>

</FormKitLazyProvider>
</template>

<style lang="scss" scoped>
@use '@/abstract/mixins' as *;
.form-title {
  width: 100%;
  margin-bottom: 72px;

  h6 {
    margin-bottom: 9px;
  }

  h1 {
    margin-bottom: 0;
  }

  .form-error {
    margin-top: 32px;
    color: var(--c-white);
    padding: 27px 29px;
    background-color: var(--c-red-90);

    display: flex;
    gap: 25px;
    align-items: center;

    icon {
      line-height: 1;
    }

    p {
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0.2px;

      margin-bottom: 0;
    }
  }

  @include breakpoint(tl) {
    margin-bottom: 96px;
  }
}

.grid-columns-2 {
  display: grid;
  padding-bottom: 65px;
  margin-bottom: 40px;
  width: 100%;

  border-bottom: 1px solid var(--c-line-light);

  grid-template-columns: 1fr;
  gap: 48px;

  @include breakpoint(tl) {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }

  h4 {
    margin-bottom: 18px;

    @include breakpoint(tl) {
      margin-bottom: 52px;
    }
  }

  .top-right {
    margin-bottom: 40px;
  }
}

.label {
  color: var(--c-primary);
  line-height: 30px;
}

.label-information {
  font-size: 16px;
  font-weight: 400;
}

.details-wrapper {
  display: grid;
  grid-template-columns: 130px 1fr;
  column-gap: 16px;
  align-items: baseline;

  border-bottom: 1px solid var(--c-line-light);
  padding: 16px 0;

  &.first {
    border-top: 1px solid var(--c-line-light);
  }

  p {
    margin-bottom: 0;
  }

  span {
    display: grid;
    line-height: 24px;
  }

  .fph-number {
    grid-column: 1 / span 2;
    display: grid;
    grid-template-columns: 130px 1fr;
  }
}

.summary-producs-wrapper {
  width: 100%;

  .product-header {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-top: 32px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--c-line-light);

    text-transform: uppercase;

    h5 {
      font-size: 13px;
    }

    .product-price-label {
      margin-left: auto;
      margin-right: 0;
    }

    @include breakpoint(tl) {
      margin-top: 24px;
      padding-bottom: 14px;

      .product-price-label {
        margin-right: 60px;
      }
    }
  }

  .total-price {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 20px 0 55px 0;
    border-bottom: 1px solid rgba(24, 36, 111, 0.1);
    padding: 15px 0 40px 0;

    p {
      font-size: 20px;
      font-weight: 700;
      margin-bottom: 0;
    }

    .right-item {
      margin-left: auto;
      display: grid;
      grid-template-rows: 1fr;
      text-align: right;
      margin-right: 0;

      .price-label {
        font-size: 20px;
      }
    }

    @include breakpoint(tl) {
      margin: 16px 0 48px 0;

      .right-item {
        margin-right: 60px;
      }
    }
  }

  .product-review-item {
    display: grid;
    grid-template-columns: 1fr auto;
    border-bottom: 1px solid var(--c-line-light);
    padding: 18px 0;
    margin-bottom: 64px;
    column-gap: 24px;

    @include breakpoint(tl) {
      grid-template-columns: 1fr 210px;
    }

    &-left {
      .title {
        color: var(--c-gray400);
        font-size: 20px;
        margin-bottom: 0;
        font-weight: 700;
      }

      .details {
        display: inline-flex;
        align-items: center;

        .date-label {
          margin-bottom: 0;
        }

        .date-label,
        .date {
          font-size: 16px;
          margin-right: 10px;
          margin-bottom: 0;
          color: var(--c-gray400);
        }
      }
    }

    &-right {
      position: relative;
      margin-left: auto;
    }
  }
}

.select-partner-wrapper {
  border-bottom: 1px solid var(--c-line-light);
  padding-bottom: 25px;
  margin-bottom: 65px;

  p {
    font-size: 16px;
  }
}

.buttons-wrapper {
  margin-top: 72px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 30px;

  @include breakpoint(tl) {
    margin-top: 80px;

    justify-content: flex-end;
    flex-direction: row;
  }

  .formkit-messages {
    margin-right: auto;

    display: flex;
    justify-content: center;
  }

  .selection-hint {
    margin-bottom: 16px;

    color: var(--c-primary);
  }
}
</style>
